import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Clock, Star, Users, Heart, Tv, TrendingUp, ListPlus, Edit, Check, X, Loader2, Search, DollarSign, Lock, Plus } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { companyApi, editRequestsApi, seriesApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AddSeasonDialog from '../components/AddSeasonDialog';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

const formatWatchTime = (seconds) => {
  if (!seconds || seconds <= 0) return '0h 0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
};

const StatCard = ({ icon: Icon, label, value, color = '#8B5CF6', sub }) => (
  <div className="admin-card p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <Icon className="w-5 h-5 mb-2" style={{ color }} />
    <p className="text-2xl font-bold tracking-tight">{value}</p>
    <p className="text-xs text-gray-400 mt-0.5">{label}</p>
    {sub && <p className="text-[10px] text-gray-500 mt-1">{sub}</p>}
  </div>
);

const RatingBar = ({ star, count, max }) => (
  <div className="flex items-center gap-2 text-sm">
    <span className="w-4 text-gray-400">{star}</span>
    <Star className="w-3 h-3 text-yellow-400" />
    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
      <div className="h-full bg-yellow-400 rounded-full transition-all" style={{ width: max > 0 ? `${(count / max) * 100}%` : '0%' }} />
    </div>
    <span className="w-8 text-right text-gray-500">{count}</span>
  </div>
);

const ContentPerformancePage = () => {
  const { filmId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [episodes, setEpisodes] = useState([]);
  const [editingEp, setEditingEp] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '' });
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [epSeasonFilter, setEpSeasonFilter] = useState('all');
  const [epSearch, setEpSearch] = useState('');
  const [showAddSeason, setShowAddSeason] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    const fetchPerformance = async () => {
      try {
        const result = await companyApi.getContentPerformance(filmId);
        setData(result);
        if (result.content_type === 'series') {
          try {
            const epData = await seriesApi.getAllEpisodes(filmId);
            setEpisodes(epData.episodes || epData || []);
          } catch { setEpisodes([]); }
        }
      } catch (error) {
        toast.error(error.message || 'Failed to load performance data');
        navigate('/company/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchPerformance();
  }, [filmId, isAuthenticated, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030014] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!data) return null;

  const maxRating = Math.max(...Object.values(data.rating_distribution || {}), 1);
  const isSeries = data.content_type === 'series';

  return (
    <div className="min-h-screen bg-[#030014]" data-testid="content-performance-page">
      <Navbar />

      <div className="pt-24 pb-12 max-w-[1400px] mx-auto px-4 md:px-8">
        {/* Header */}
        <button onClick={() => navigate('/company/dashboard')} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" /> Back to Dashboard
        </button>

        <div className="flex items-start gap-6 mb-8">
          {data.thumbnail && (
            <img src={data.thumbnail} alt={data.title} className="w-48 h-28 object-cover rounded-lg flex-shrink-0" />
          )}
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{data.title}</h1>
              {isSeries && (
                <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-[#00FF9D]/20 text-[#00FF9D] flex items-center gap-1">
                  <Tv className="w-3 h-3" /> Series
                </span>
              )}
            </div>
            <p className="text-sm text-gray-400">
              Published {data.created_at ? new Date(data.created_at).toLocaleDateString('en-US') : 'N/A'}
              {isSeries && ` - ${data.episodes_count} episodes`}
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <StatCard icon={Eye} label="Total Views" value={data.views?.toLocaleString() || '0'} color="#8B5CF6" />
          <StatCard icon={Heart} label="Total Likes" value={data.total_likes?.toLocaleString() || '0'} color="#EC4899" />
          <StatCard icon={Clock} label="Watch Time" value={formatWatchTime(data.total_watch_seconds)} color="#3B82F6" sub={`${data.total_watch_sessions || 0} sessions`} />
          <StatCard icon={Users} label="Unique Viewers" value={data.unique_watchers || '0'} color="#00FF9D" />
          <StatCard icon={Star} label="Avg Rating" value={data.avg_rating?.toFixed(1) || '0.0'} color="#F59E0B" sub={`${data.rating_count || 0} ratings`} />
        </div>

        {/* Monetization Metrics */}
        {data.monetization && (() => {
          const m = data.monetization;

          const RevenueCard = ({ icon: Icon, iconBg, iconColor, label, count, netEarnings, testId }) => (
            <div className="admin-card p-5" data-testid={testId}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBg}`}>
                  <Icon className={`w-5 h-5 ${iconColor}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-400">{label}</p>
                </div>
              </div>
              <p className="text-2xl font-bold tracking-tight mb-3">${netEarnings.toFixed(2)}</p>
              <div className="flex items-center justify-between text-sm border-t border-white/5 pt-3">
                <span className="text-gray-500">Transactions</span>
                <span className="text-gray-300 font-medium tabular-nums">{count}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-gray-500">Net earnings</span>
                <span className="text-[#00FF9D] font-medium tabular-nums">${netEarnings.toFixed(2)}</span>
              </div>
            </div>
          );

          return (
            <div className="mb-8" data-testid="monetization-metrics">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#00FF9D]" /> Revenue from this Content
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <RevenueCard
                  icon={Heart} iconBg="bg-pink-500/15" iconColor="text-pink-400"
                  label="Donations" count={m.donations.count} netEarnings={m.donations.creator_total}
                  testId="donations-metric"
                />
                {data.is_premium_content && (
                  <RevenueCard
                    icon={Lock} iconBg="bg-[#8B5CF6]/15" iconColor="text-[#8B5CF6]"
                    label="Pay-Per-View" count={m.ppv.count} netEarnings={m.ppv.creator_total}
                    testId="ppv-metric"
                  />
                )}
                <RevenueCard
                  icon={DollarSign} iconBg="bg-amber-500/15" iconColor="text-amber-400"
                  label="Total from this Content"
                  count={m.donations.count + (data.is_premium_content ? m.ppv.count : 0)}
                  netEarnings={m.donations.creator_total + (data.is_premium_content ? m.ppv.creator_total : 0)}
                  testId="total-content-revenue-metric"
                />
              </div>
            </div>
          );
        })()}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Watch Time Chart */}
          <div className="lg:col-span-2 admin-card p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#8B5CF6]" /> Watch Activity (Last 30 Days)
            </h2>
            {data.daily_watch_data?.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.daily_watch_data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a1a3a" />
                  <XAxis dataKey="date" tick={{ fill: '#666', fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fill: '#666', fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: '#0F0B1E', border: '1px solid rgba(139,92,246,0.3)', borderRadius: '8px', color: '#fff' }} />
                  <Area type="monotone" dataKey="hours" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.15} name="Watch Hours" />
                  <Area type="monotone" dataKey="sessions" stroke="#00FF9D" fill="#00FF9D" fillOpacity={0.1} name="Sessions" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[260px] flex items-center justify-center text-gray-500 text-sm">No watch data available yet</div>
            )}
          </div>

          {/* Ratings Distribution */}
          <div className="admin-card p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400" /> Rating Distribution
            </h2>
            <div className="space-y-3 mb-6">
              {[5, 4, 3, 2, 1].map(star => (
                <RatingBar key={star} star={star} count={data.rating_distribution?.[star] || 0} max={maxRating} />
              ))}
            </div>
            <div className="pt-4 border-t border-white/10">
              <div className="flex items-center gap-2">
                <ListPlus className="w-4 h-4 text-[#8B5CF6]" />
                <span className="text-sm text-gray-400">Added to {data.list_additions || 0} user lists</span>
              </div>
            </div>
          </div>
        </div>

        {/* Episode Stats for Series */}
        {isSeries && data.episode_stats?.length > 0 && (
          <div className="admin-card p-6 mt-6" data-testid="episode-performance">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Tv className="w-5 h-5 text-[#00FF9D]" /> Episode Performance
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-gray-400 text-left">
                    <th className="py-2.5 pr-4">Episode</th>
                    <th className="py-2.5 pr-4 text-center">Views</th>
                    <th className="py-2.5 pr-4 text-center">Likes</th>
                    <th className="py-2.5 text-center">Watch Time</th>
                  </tr>
                </thead>
                <tbody>
                  {data.episode_stats.map(ep => (
                    <tr key={ep.episode_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="py-2.5 pr-4 font-medium">
                        S{ep.season_number}E{ep.episode_number}: {ep.title}
                      </td>
                      <td className="py-2.5 pr-4 text-center text-gray-300">{(ep.views || 0).toLocaleString()}</td>
                      <td className="py-2.5 pr-4 text-center text-pink-400">{(ep.likes || 0).toLocaleString()}</td>
                      <td className="py-2.5 text-center text-blue-400">{formatWatchTime(ep.watch_seconds)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Episode Management for Series */}
        {isSeries && episodes.length > 0 && (() => {
          const seasonNumbers = [...new Set(episodes.map(ep => ep.season_number))].sort((a, b) => a - b);
          const filteredEps = episodes.filter(ep => {
            if (epSeasonFilter !== 'all' && String(ep.season_number) !== epSeasonFilter) return false;
            if (epSearch.trim() && !ep.title?.toLowerCase().includes(epSearch.toLowerCase()) && !`S${ep.season_number}E${ep.episode_number}`.toLowerCase().includes(epSearch.toLowerCase())) return false;
            return true;
          });
          return (
          <>
          <div className="admin-card p-6 mt-6" data-testid="episode-management">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Edit className="w-5 h-5 text-[#8B5CF6]" /> Episode Management
              </h2>
              <Button size="sm" variant="outline" onClick={() => setShowAddSeason(true)}
                className="border-[#00FF9D]/30 text-[#00FF9D] hover:bg-[#00FF9D]/10" data-testid="add-season-perf-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Season
              </Button>
            </div>
            <p className="text-sm text-gray-400 mb-4">Edit episode titles and descriptions. All changes require admin approval before going live.</p>

            <div className="flex flex-wrap gap-3 mb-4">
              <Select value={epSeasonFilter} onValueChange={v => setEpSeasonFilter(v)} data-testid="ep-season-filter">
                <SelectTrigger className="w-[160px] bg-[#030014] border-[#8B5CF6]/20 h-9 text-sm text-gray-300" data-testid="ep-season-filter-trigger">
                  <SelectValue placeholder="All Seasons" />
                </SelectTrigger>
                <SelectContent className="bg-[#0F0B1E] border-[#8B5CF6]/20">
                  <SelectItem value="all" data-testid="ep-season-all">All Seasons</SelectItem>
                  {seasonNumbers.map(s => (
                    <SelectItem key={s} value={String(s)} data-testid={`ep-season-${s}`}>Season {s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  value={epSearch}
                  onChange={e => setEpSearch(e.target.value)}
                  placeholder="Search episodes..."
                  className="w-full bg-[#030014] border border-[#8B5CF6]/20 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-300 placeholder-gray-600"
                  data-testid="ep-search-input"
                />
              </div>
              <span className="text-xs text-gray-500 self-center">{filteredEps.length} of {episodes.length} episodes</span>
            </div>

            {filteredEps.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No episodes match your filters</p>
            ) : (
            <div className="space-y-3">
              {filteredEps.map(ep => (
                <div key={ep.episode_id} className={`p-4 rounded-lg border ${ep.status === 'rejected' ? 'bg-red-500/5 border-red-500/20' : 'bg-[#0F0B1E] border-white/5'}`} data-testid={`episode-row-${ep.episode_id}`}>
                  {editingEp === ep.episode_id ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-xs text-[#00FF9D] font-medium mb-2">
                        S{ep.season_number}E{ep.episode_number}
                      </div>
                      <Input
                        value={editForm.title}
                        onChange={e => setEditForm(p => ({...p, title: e.target.value}))}
                        placeholder="Episode title"
                        className="bg-[#030014] border-[#8B5CF6]/20 h-10"
                        data-testid={`episode-title-input-${ep.episode_id}`}
                      />
                      <Textarea
                        value={editForm.description}
                        onChange={e => setEditForm(p => ({...p, description: e.target.value}))}
                        placeholder="Episode description"
                        className="bg-[#030014] border-[#8B5CF6]/20 min-h-[80px]"
                        data-testid={`episode-desc-input-${ep.episode_id}`}
                      />
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => setEditingEp(null)}
                          className="border-white/10" data-testid={`episode-cancel-${ep.episode_id}`}>
                          <X className="w-3.5 h-3.5 mr-1" /> Cancel
                        </Button>
                        <Button size="sm" disabled={submittingEdit}
                          className="bg-[#8B5CF6] hover:bg-[#7C3AED]"
                          data-testid={`episode-submit-${ep.episode_id}`}
                          onClick={async () => {
                            setSubmittingEdit(true);
                            try {
                              if (ep.status === 'approved') {
                                await editRequestsApi.submitEpisodeEdit(ep.episode_id, editForm);
                                toast.success('Edit submitted for owner review');
                              } else {
                                await editRequestsApi.updateAndResubmitEpisode(ep.episode_id, editForm);
                                toast.success('Episode updated and resubmitted for review');
                                // Refresh episodes list
                                const epData = await seriesApi.getAllEpisodes(filmId);
                                setEpisodes(epData.episodes || epData || []);
                              }
                              setEditingEp(null);
                            } catch (e) { toast.error(e.message || 'Failed to submit edit'); }
                            finally { setSubmittingEdit(false); }
                          }}>
                          {submittingEdit ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                          {ep.status === 'approved' ? 'Submit for Review' : 'Resubmit'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-[#00FF9D]">S{ep.season_number}E{ep.episode_number}</span>
                            <span className="font-medium">{ep.title}</span>
                            {ep.status && ep.status !== 'approved' && (
                              <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${
                                ep.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                ep.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>{ep.status?.toUpperCase()}</span>
                            )}
                          </div>
                          {ep.description && <p className="text-xs text-gray-500 line-clamp-2">{ep.description}</p>}
                        </div>
                        <Button size="sm" variant="outline"
                          className="border-[#8B5CF6]/30 flex-shrink-0"
                          data-testid={`episode-edit-btn-${ep.episode_id}`}
                          onClick={() => {
                            setEditingEp(ep.episode_id);
                            setEditForm({ title: ep.title || '', description: ep.description || '' });
                          }}>
                          <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                        </Button>
                      </div>
                      {ep.status === 'rejected' && ep.rejection_feedback && (
                        <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
                          <strong>Feedback:</strong> {ep.rejection_feedback}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            )}
          </div>
          <AddSeasonDialog
            open={showAddSeason}
            onOpenChange={setShowAddSeason}
            seriesId={filmId}
            seriesTitle={data.title}
            existingSeasons={seasonNumbers}
            onSeasonAdded={async () => {
              try {
                const epData = await seriesApi.getAllEpisodes(filmId);
                setEpisodes(epData.episodes || epData || []);
              } catch {}
            }}
          />
          </>
          );
        })()}
      </div>
    </div>
  );
};

export default ContentPerformancePage;
